export interface ParseState {
  buffer: string;
}

export interface ReasoningInterval {
  start: number; // ms timestamp from API
  end: number; // ms timestamp from API
  id?: string; // reasoning part id, when the API provides one
}

export interface ParsedChunk {
  content: string;
  thinking: string;
  // Raw reasoning intervals seen in this chunk. Reasoning streams as repeated
  // snapshots of the same block (shared start, growing end), so durations must
  // be merged by block downstream — never summed per event.
  thinkingIntervals: ReasoningInterval[];
}

export function createParseState(): ParseState {
  return { buffer: "" };
}

type ToolState = {
  status?: string;
  title?: string;
  input?: Record<string, unknown>;
  output?: string;
  metadata?: { output?: string; exit?: number };
  time?: { start: number; end: number };
};

type OpenCodeEvent = {
  type: string;
  timestamp?: number;
  sessionID?: string;
  part?: {
    type?: string;
    text?: string;
    tool?: string;
    state?: ToolState;
    time?: { start: number; end: number };
    [key: string]: unknown;
  };
};

type ParsedLine =
  | { kind: "content"; text: string }
  | {
      kind: "thinking";
      text: string;
      time?: { start: number; end: number };
      id?: string;
    }
  | null;

function eventToLine(event: OpenCodeEvent): ParsedLine {
  const { type, part } = event;

  switch (type) {
    case "text": {
      const text = part?.text?.trim();
      return text ? { kind: "content", text } : null;
    }

    case "reasoning": {
      const text = part?.text?.trim();
      if (!text) return null;
      const id = typeof part?.id === "string" ? part.id : undefined;
      return { kind: "thinking", text, time: part?.time, id };
    }

    case "tool_use": {
      const state = part?.state;

      if (
        state?.status &&
        state.status !== "completed" &&
        state.status !== "error"
      ) {
        return null;
      }

      const toolName = part?.tool ?? "tool";
      const title =
        state?.title ?? (state?.input?.description as string | undefined);

      const desc = title ? `: *${title}*` : "";
      return { kind: "content", text: `**🔧 \`${toolName}\`${desc}**` };
    }

    case "step_start":
    case "step_finish":
    case "message_start":
    case "message_finish":
    case "session_start":
    case "session_finish":
    case "reasoning.encrypted":
    case "tool-call-streaming-start":
    case "tool-call-delta":
      return null;

    default:
      return null;
  }
}

function parseLine(line: string): ParsedLine {
  const trimmed = line.trim();
  if (!trimmed) return null;

  let event: OpenCodeEvent;
  try {
    event = JSON.parse(trimmed) as OpenCodeEvent;
  } catch {
    return { kind: "content", text: trimmed };
  }

  return eventToLine(event);
}

export function processChunk(chunk: string, state: ParseState): ParsedChunk {
  state.buffer += chunk;
  const lines = state.buffer.split("\n");
  state.buffer = lines.pop() ?? "";

  const contentParts: string[] = [];
  const thinkingParts: string[] = [];
  const thinkingIntervals: ReasoningInterval[] = [];

  for (const line of lines) {
    const result = parseLine(line);
    if (!result) continue;
    if (result.kind === "content") {
      contentParts.push(result.text);
    } else {
      thinkingParts.push(result.text);
      const interval = toInterval(result);
      if (interval) thinkingIntervals.push(interval);
    }
  }

  return {
    content: contentParts.length ? contentParts.join("\n\n") + "\n\n" : "",
    thinking: thinkingParts.join("\n\n"),
    thinkingIntervals,
  };
}

function toInterval(result: {
  time?: { start: number; end: number };
  id?: string;
}): ReasoningInterval | null {
  const { time, id } = result;
  if (!time || !Number.isFinite(time.start) || !Number.isFinite(time.end)) {
    return null;
  }
  return { start: time.start, end: time.end, id };
}

export function flushBuffer(state: ParseState): ParsedChunk {
  const result = parseLine(state.buffer);
  state.buffer = "";
  if (!result) return { content: "", thinking: "", thinkingIntervals: [] };

  if (result.kind === "content") {
    return { content: result.text, thinking: "", thinkingIntervals: [] };
  }

  const interval = toInterval(result);

  return {
    content: "",
    thinking: result.text,
    thinkingIntervals: interval ? [interval] : [],
  };
}

export function parseFullOutput(output: string): string {
  const state = createParseState();
  const body = processChunk(output, state);
  const tail = flushBuffer(state);
  return [body.content, tail.content].filter(Boolean).join("\n\n").trim();
}
